import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { loadTalent, patchTalent } from '../../../utils/Network';
import { mediaSrc } from '../../../utils';
import Gallery from '../../Gallery';

const emptyDraft = () => ({ title: '', image_url: '', url: '' });

function normalizeCourses(raw) {
    if (Array.isArray(raw)) return raw.map((c) => ({
        title: c.title != null ? String(c.title) : '',
        image_url: c.image_url != null ? String(c.image_url) : '',
        url: c.url != null ? String(c.url) : '',
    }));
    return [];
}

export default function TalentAcademyCoursesWidget({ talentId }) {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    /** -1 = add new, >= 0 = edit index */
    const [editingIndex, setEditingIndex] = useState(-1);
    const [draft, setDraft] = useState(emptyDraft);
    const [modalError, setModalError] = useState(null);

    useEffect(() => {
        if (!talentId) return;
        setLoading(true);
        setError(null);
        loadTalent(talentId)
            .then((talent) => {
                setCourses(normalizeCourses(talent.academy_courses));
            })
            .catch(() => setError('Failed to load talent.'))
            .finally(() => setLoading(false));
    }, [talentId]);

    const setSelectedImage = (galleryItem) => {
        const url = galleryItem?.image || '';
        setDraft((d) => ({ ...d, image_url: url }));
        setGalleryOpen(false);
    };

    const openAddModal = () => {
        setDraft(emptyDraft());
        setEditingIndex(-1);
        setModalError(null);
        setModalOpen(true);
    };

    const openEditModal = (index) => {
        const row = courses[index];
        if (!row) return;
        setDraft({ title: row.title, image_url: row.image_url, url: row.url });
        setEditingIndex(index);
        setModalError(null);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingIndex(-1);
        setDraft(emptyDraft());
        setModalError(null);
    };

    const handleModalSave = () => {
        const next = [...courses];
        if (editingIndex === -1) {
            next.push({ ...draft });
        } else {
            next[editingIndex] = { ...draft };
        }
        setSaving(true);
        setModalError(null);
        patchTalent(talentId, { academy_courses: next })
            .then(() => {
                setCourses(next);
                closeModal();
            })
            .catch(() => setModalError('Save failed.'))
            .finally(() => setSaving(false));
    };

    const handleModalDelete = () => {
        if (editingIndex < 0) return;
        const next = courses.filter((_, i) => i !== editingIndex);
        setSaving(true);
        setModalError(null);
        patchTalent(talentId, { academy_courses: next })
            .then(() => {
                setCourses(next);
                closeModal();
            })
            .catch(() => setModalError('Could not delete.'))
            .finally(() => setSaving(false));
    };

    const updateDraft = (field, value) => {
        setDraft((d) => ({ ...d, [field]: value }));
    };

    if (!talentId) return null;

    return (
        <div className="">
            <Gallery
                setSelectedGalleryItem={setSelectedImage}
                isOpen={galleryOpen}
                key={galleryOpen}
                closeGallery={() => setGalleryOpen(false)}
            />
            <div className="box-body">
                
                {loading && <p>Loading…</p>}
                {!loading && (
                    <>
                        <div className="cms-academy-courses-toolbar" style={{ marginBottom: 16 }}>
                            <button type="button" className="btn btn-primary" onClick={openAddModal}>
                                <i className="fa fa-plus" />
                            </button>
                        </div>
                        {courses.length === 0 ? (
                            <p className="text-muted">No courses yet. Click &quot;Add course&quot;.</p>
                        ) : (
                            <div className="cms-academy-courses-grid">
                                {courses.map((row, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="cms-academy-courses-card"
                                        onClick={() => openEditModal(index)}
                                    >
                                        <div className="cms-academy-courses-card-image-wrap">
                                            <img
                                                alt=""
                                                src={mediaSrc(row.image_url)}
                                            />
                                        </div>
                                        <div className="cms-academy-courses-card-title">
                                            {row.title || 'Untitled'}
                                        </div>
                                        {row.url ? (
                                            <span className="cms-academy-courses-card-url-hint text-muted">
                                                <i className="fa fa-link" /> Link set
                                            </span>
                                        ) : null}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
                {error && <div className="alert alert-danger margin-t-10">{error}</div>}
            </div>

            <ReactModal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                shouldCloseOnOverlayClick
                shouldCloseOnEsc
                className="Modal Modal--cms-gallery Modal--cms-academy-course"
                overlayClassName="Overlay Overlay--cms-gallery Overlay--cms-academy-course"
                ariaHideApp={false}
                style={{
                    overlay: { zIndex: 1980 },
                    content: {
                        zIndex: 1981,
                        width: '92%',
                        maxWidth: '460px',
                        inset: '50% auto auto 50%',
                        transform: 'translate(-50%, -50%)',
                    },
                }}
            >
                <div className="modal-content cms-gallery-modal-content">
                    <div className="modal-body gallery padding-0 cms-gallery-modal-body cms-academy-course-modal">
                        
                        <div className="cms-gallery-shell">
                            
                            <div className="cms-gallery-upload-panel">
                                <div className="cms-gallery-upload-stack">
                                    <button
                                        type="button"
                                        className="cms-gallery-dropzone cms-academy-course-modal__image"
                                        onClick={() => setGalleryOpen(true)}
                                    >
                                        {draft.image_url ? (
                                            <>
                                                <img
                                                    src={mediaSrc(draft.image_url, draft.image_url)}
                                                    alt=""
                                                    className="cms-gallery-dropzone-preview"
                                                />
                                                <button
                                                    type="button"
                                                    className="cms-gallery-dropzone-clear"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateDraft('image_url', '');
                                                    }}
                                                    title="Remove image"
                                                >
                                                    <i className="fa fa-trash" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-picture-o" />
                                                <span>Choose image from gallery</span>
                                                <small>Opens the media library</small>
                                            </>
                                        )}
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control cms-gallery-input"
                                        value={draft.title}
                                        onChange={(e) => updateDraft('title', e.target.value)}
                                        placeholder="Course title"
                                        autoComplete="off"
                                    />
                                    <input
                                        type="url"
                                        className="form-control cms-gallery-input"
                                        value={draft.url}
                                        onChange={(e) => updateDraft('url', e.target.value)}
                                        placeholder="https://…"
                                        autoComplete="off"
                                    />
                                    {modalError ? <div className="error">{modalError}</div> : null}
                                    <div className="cms-academy-course-modal__actions">
                                        {editingIndex >= 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-cm btn-cm-danger"
                                                onClick={handleModalDelete}
                                                disabled={saving}
                                            >
                                                <i className="fa fa-trash margin-r-5" />
                                                Delete
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-default"
                                            onClick={closeModal}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-cm"
                                            onClick={handleModalSave}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>Saving…</>
                                            ) : (
                                                <>
                                                    <i className="fa fa-check margin-r-5" />
                                                    Save
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ReactModal>
        </div>
    );
}
